// ==============================
// Mobile card view for the coach-facing class students table
// ==============================
// On phones the wide student table (with bulk-select + check-in) becomes a list
// of cards. Preserves the selection checkbox and the history / check-in actions.
// Used behind useIsMobile() inside CoachClass; the desktop table is unchanged.

import * as React from 'react';
import { Cancel, CheckCircle, Done, Visibility } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Card,
  Checkbox,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { CoachClassStudent } from '../types/CoachClass.type';
import {
  serviceColors,
  serviceTypeColors,
  userTypeColors,
} from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import SofLevelChip from './common/SofLevelChip';

const checkInStatusColors: Record<string, string> = {
  CHECKED_IN: '#11734b',
  CHECKED_IN_BG: '#d4edbc',
  NOT_CHECKED_IN: '#bc1e1d',
  NOT_CHECKED_IN_BG: '#ffcfc9',
};

const formatStudentDate = (value: string): string => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '---'
    : date.toLocaleDateString('en-US');
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Box>
);

type CoachStudentCardListProps = {
  students: CoachClassStudent[];
  selectedStudentIds: Set<number>;
  checkInSubmitting: boolean;
  allSelected: boolean;
  someSelected: boolean;
  selectAllDisabled: boolean;
  isExpired: (student: CoachClassStudent) => boolean;
  onSelectAll: (checked: boolean) => void;
  onToggleSelect: (registrationId: number, checked: boolean) => void;
  onOpenPackage: (event: React.MouseEvent, student: CoachClassStudent) => void;
  onOpenHistory: (event: React.MouseEvent, student: CoachClassStudent) => void;
  onOpenCheckIn: (event: React.MouseEvent, student: CoachClassStudent) => void;
};

const CoachStudentCardList = ({
  students,
  selectedStudentIds,
  checkInSubmitting,
  allSelected,
  someSelected,
  selectAllDisabled,
  isExpired,
  onSelectAll,
  onToggleSelect,
  onOpenPackage,
  onOpenHistory,
  onOpenCheckIn,
}: CoachStudentCardListProps) => {
  if (students.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No students found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {/* Select-all — the desktop header checkbox isn't present on mobile. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          disabled={selectAllDisabled}
          onChange={(event) => onSelectAll(event.target.checked)}
          inputProps={{ 'aria-label': 'Select all students' }}
        />
        <Typography variant="body2" color="text.secondary">
          Select all
        </Typography>
      </Box>
      {students.map((student) => (
        <Card
          key={student.id}
          variant="outlined"
          sx={(theme) => ({
            p: 1.5,
            ...(isExpired(student) && {
              backgroundColor: alpha(theme.palette.warning.main, 0.18),
            }),
          })}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5, mt: -0.5 }}
                checked={selectedStudentIds.has(student.registrationId)}
                disabled={student.checkedInToday || checkInSubmitting}
                onChange={(event) =>
                  onToggleSelect(student.registrationId, event.target.checked)
                }
                inputProps={{ 'aria-label': `Select ${student.name}` }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {student.lastName} {student.firstName}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <ColorChip
                    label={
                      student.checkedInToday ? 'Checked In' : 'Not Checked In'
                    }
                    palette={checkInStatusColors}
                    paletteKey={
                      student.checkedInToday ? 'CHECKED_IN' : 'NOT_CHECKED_IN'
                    }
                    icon={
                      student.checkedInToday ? (
                        <CheckCircle sx={{ fontSize: 16 }} />
                      ) : (
                        <Cancel sx={{ fontSize: 16 }} />
                      )
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <Tooltip title="View check-in history">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`View check-in history for ${student.name}`}
                  onClick={(event) => onOpenHistory(event, student)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  student.checkedInToday
                    ? 'Already checked in today'
                    : 'Check in'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={student.checkedInToday}
                    aria-label={`Check in ${student.name}`}
                    onClick={(event) => onOpenCheckIn(event, student)}
                  >
                    <Done fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Field label="Preferred">{student.preferredName || 'None'}</Field>
          <Field label="Level">
            <SofLevelChip level={student.level} serviceType={student.type} />
          </Field>
          <Field label="User Type">
            <ColorChip label={student.userType} palette={userTypeColors} />
          </Field>
          <Field label="Program">
            <ColorChip label={student.program} palette={serviceColors} />
          </Field>
          <Field label="Type">
            <ColorChip label={student.type} palette={serviceTypeColors} />
          </Field>
          <Field label="Package">
            <ButtonBase
              onClick={(event) => onOpenPackage(event, student)}
              sx={{
                p: 0,
                font: 'inherit',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              {student.details}
            </ButtonBase>
          </Field>
          <Field label="Sessions Left">
            {student.visitType === 'count'
              ? (student.remainingSessions ?? '---')
              : '∞'}
          </Field>
          <Field label="Joined">{formatStudentDate(student.joinedDate)}</Field>
        </Card>
      ))}
    </Stack>
  );
};

export default CoachStudentCardList;
